/**
 * Business OS Collaboration — Comment Thread widget.
 *
 * Reused by:
 *   - Business OS Portal (recipient posts comments/approvals)
 *   - Business OS Dashboard (owner replies, resolves)
 *
 * Actions supported:
 *   comment · reply · approve · request_revision · resolve
 *
 * All writes go through `businessOsPortalAPI.createComment` (recipient)
 * or `businessOsAPI.ownerReply` (owner).
 */
import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import {
  CheckCircle, PaperPlaneTilt, WarningCircle, ArrowUUpLeft,
  ChatCircleText, Check,
} from '@phosphor-icons/react';

const ROLE_TINT = {
  recipient: { bg: '#F0FDF4', border: '#22C55E', text: '#166534' },
  owner: { bg: '#EEF2FF', border: '#6366F1', text: '#3730A3' },
};

const ACTION_LABEL = {
  comment: 'Comment',
  reply: 'Reply',
  approve: 'Approved',
  request_revision: 'Revision requested',
  resolve: 'Resolved',
};

const ACTION_ICON = {
  approve: <Check size={12} weight="bold" />,
  request_revision: <WarningCircle size={12} weight="fill" />,
  resolve: <CheckCircle size={12} weight="fill" />,
};

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

const CommentRow = ({ comment, onResolve, canResolve }) => {
  const role = comment.author_role || 'recipient';
  const tint = ROLE_TINT[role] || ROLE_TINT.recipient;
  const initials = ((comment.author_name || comment.author_email || '?')
    .trim().slice(0, 2)).toUpperCase();
  const isAction = ['approve', 'request_revision', 'resolve'].includes(comment.action);
  const resolved = comment.status === 'resolved';

  return (
    <div
      className="flex gap-3 items-start"
      data-testid={`comment-row-${comment.id}`}
    >
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold"
        style={{ background: tint.bg, color: tint.text, border: `1px solid ${tint.border}` }}
      >
        {initials}
      </div>
      <div className={`flex-1 min-w-0 rounded-lg px-3 py-2 ${resolved ? 'opacity-60' : ''}`}
           style={{ background: tint.bg, border: `1px solid ${tint.border}33` }}>
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-sm font-semibold" style={{ color: tint.text }}>
            {comment.author_name || comment.author_email || 'Guest'}
          </span>
          {isAction && (
            <span
              className="inline-flex items-center gap-1 text-[10px] uppercase font-bold rounded-full px-2 py-0.5"
              style={{ background: tint.border, color: '#fff' }}
            >
              {ACTION_ICON[comment.action]} {ACTION_LABEL[comment.action]}
            </span>
          )}
          {resolved && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold rounded-full px-2 py-0.5 bg-slate-200 text-slate-600">
              <CheckCircle size={10} weight="fill" /> Resolved
            </span>
          )}
          <span className="text-[11px] text-slate-500">{formatDate(comment.created_at)}</span>
        </div>
        {comment.body && (
          <div className="text-sm text-slate-800 whitespace-pre-wrap" data-testid={`comment-body-${comment.id}`}>
            {comment.body}
          </div>
        )}
        {canResolve && !resolved && (
          <button
            type="button"
            onClick={() => onResolve(comment.id)}
            className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800"
            data-testid={`comment-resolve-${comment.id}`}
          >
            <CheckCircle size={12} /> Mark resolved
          </button>
        )}
      </div>
    </div>
  );
};

export const CommentThread = ({
  comments,
  onSubmit,
  onResolve,
  authorName = '',
  authorEmail = '',
  canComment = true,
  canApprove = true,
  canResolve = true,
  accentColor = '#22C55E',
  hideForm = false,
  compact = false,
  testid = 'comment-thread',
}) => {
  const [body, setBody] = useState('');
  const [name, setName] = useState(authorName);
  const [email, setEmail] = useState(authorEmail);
  const [busy, setBusy] = useState(false);

  const sorted = useMemo(() =>
    (comments || []).slice().sort((a, b) => (a.created_at || '').localeCompare(b.created_at || '')),
  [comments]);

  const submit = async (action) => {
    if (!canComment) return;
    if (action === 'comment' && !body.trim()) {
      toast.error('Please enter a comment.');
      return;
    }
    if (!name.trim() && !email.trim()) {
      toast.error('Please enter your name or email so the owner knows who commented.');
      return;
    }
    setBusy(true);
    try {
      await onSubmit({
        body: body.trim(),
        action,
        author_name: name.trim(),
        author_email: email.trim(),
      });
      setBody('');
      toast.success(action === 'comment' ? 'Comment posted' : `${ACTION_LABEL[action]} recorded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not post — try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'} data-testid={testid}>
      <div className="flex items-center gap-2 text-sm text-slate-600" data-testid={`${testid}-count`}>
        <ChatCircleText size={16} />
        <span>{sorted.length} {sorted.length === 1 ? 'comment' : 'comments'}</span>
      </div>

      {sorted.length > 0 && (
        <div className="space-y-3">
          {sorted.map((c) => (
            <CommentRow
              key={c.id}
              comment={c}
              canResolve={canResolve}
              onResolve={onResolve}
            />
          ))}
        </div>
      )}

      {!hideForm && canComment && (
        <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid={`${testid}-form`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              type="text"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={busy}
              className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm w-full"
              data-testid={`${testid}-name`}
            />
            <input
              type="email"
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
              className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm w-full"
              data-testid={`${testid}-email`}
            />
          </div>
          <textarea
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={busy}
            placeholder="Share feedback, ask a question, or leave a note…"
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm resize-y"
            data-testid={`${testid}-body`}
          />
          <div className="flex flex-wrap gap-2 justify-end">
            {canApprove && (
              <>
                <button
                  type="button"
                  onClick={() => submit('request_revision')}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 text-sm text-amber-700 border border-amber-300 rounded-md px-3 py-1.5 hover:bg-amber-50 disabled:opacity-50"
                  data-testid={`${testid}-request-revision`}
                >
                  <WarningCircle size={14} /> Request revision
                </button>
                <button
                  type="button"
                  onClick={() => submit('approve')}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 text-sm text-emerald-700 border border-emerald-300 rounded-md px-3 py-1.5 hover:bg-emerald-50 disabled:opacity-50"
                  data-testid={`${testid}-approve`}
                >
                  <Check size={14} /> Approve
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => submit('comment')}
              disabled={busy}
              className="inline-flex items-center gap-1.5 text-sm text-white rounded-md px-3 py-1.5 disabled:opacity-50"
              style={{ background: accentColor }}
              data-testid={`${testid}-submit`}
            >
              <PaperPlaneTilt size={14} /> Post
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentThread;
